/**
 * WhyOceanNova — combined credibility statement and client categories,
 * merged into one editorial section to avoid repetitive card grids.
 * Facts unchanged: the 25+ years figure is attributed to the team.
 */

import { memo } from "react";
import { useTranslation } from "react-i18next";
import Reveal from "@/components/common/Reveal";

interface Item {
  title: string;
  description: string;
}

const WhyOceanNova = () => {
  const { t } = useTranslation();
  const items = t("home.why.items", { returnObjects: true }) as Item[];
  const clients = t("home.clients.items", { returnObjects: true }) as string[];

  return (
    <section
      id="why-oceannova"
      className="bg-background py-20 md:py-28 scroll-mt-20"
      aria-labelledby="why-oceannova-heading"
    >
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="flex items-center gap-4 mb-5">
                <span className="rule w-10 shrink-0" aria-hidden="true" />
                <p className="eyebrow text-cobalt">{t("home.why.label")}</p>
              </div>
              <h2 id="why-oceannova-heading" className="display-lg text-foreground mb-8 md:mb-10">
                {t("home.why.heading")}
              </h2>
            </Reveal>

            <dl className="border-t border-foreground/20">
              {items.map((item, index) => (
                <Reveal
                  key={item.title}
                  delay={index * 70}
                  className="grid grid-cols-[auto_1fr] gap-4 md:gap-8 border-b border-foreground/15 py-5"
                >
                  <span className="index-num text-sm text-cobalt pt-1 w-8">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <dt className="text-base md:text-lg font-bold tracking-tight text-foreground">
                      {item.title}
                    </dt>
                    <dd className="text-sm md:text-base text-muted-foreground mt-1 leading-relaxed">
                      {item.description}
                    </dd>
                  </div>
                </Reveal>
              ))}
            </dl>
          </div>

          <Reveal
            delay={120}
            className="lg:col-span-5 lg:border-l lg:border-border lg:pl-10"
          >
            <h3
              id="who-we-work-with"
              className="eyebrow text-muted-foreground mb-6 scroll-mt-24"
            >
              {t("home.clients.heading")}
            </h3>
            <ul className="flex flex-wrap gap-2">
              {clients.map((label) => (
                <li
                  key={label}
                  className="border border-foreground/20 px-3.5 py-2 text-sm md:text-base font-semibold text-foreground/90 transition-colors duration-200 hover:border-cobalt hover:text-cobalt"
                >
                  {label}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default memo(WhyOceanNova);
